import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Send, CheckCircle2, Shield, Loader2, MessageSquare, HelpCircle } from 'lucide-react';
import { submitContactMessage } from '../lib/api';
import { usePageTitle } from '../hooks/usePageTitle';

const SUBJECT_OPTIONS = [
  'Question sur le fonctionnement',
  "Question sur l'abonnement",
  "Besoin d'aide",
  'Signaler un problème',
  'Partenariat',
  'Autre',
];

const PROFILE_OPTIONS = [
  'Enseignant',
  'Formateur',
  'Établissement',
  'Utilisateur intéressé',
  'Autre',
];

const HELP_ITEMS = [
  'comprendre comment fonctionne QCMAntitriche',
  "poser une question sur l'abonnement",
  'signaler un bug ou un problème',
  'proposer un partenariat',
  "demander une précision avant de vous inscrire",
];

const REASSURANCE = [
  'Réponse claire et personnalisée',
  'Aucune démarche compliquée',
  'Vos données utilisées uniquement pour traiter votre demande',
];

export default function Contact() {
  usePageTitle('Contact');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    profile: '',
    subject: '',
    message: '',
    company: '',
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Veuillez renseigner votre prénom.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = 'Veuillez saisir une adresse email valide.';
    if (!form.subject) e.subject = 'Veuillez sélectionner un sujet.';
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = 'Veuillez décrire votre demande (10 caractères minimum).';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await submitContactMessage(form);
      setSent(true);
      toast.success('Demande envoyée !');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Une erreur est survenue lors de l\'envoi. Merci de réessayer.');
    }
    setSending(false);
  };

  const set = (key, val) => {
    setForm(p => ({ ...p, [key]: val }));
    if (errors[key]) setErrors(p => ({ ...p, [key]: undefined }));
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-background">
        <ContactHeader />
        <main className="max-w-3xl mx-auto px-4 py-16" data-testid="contact-success">
          <Card className="bg-card border-border border-emerald-500/30">
            <CardContent className="p-12 text-center">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-emerald-400" />
              <h2 className="text-2xl font-black mb-3">Demande envoyée !</h2>
              <p className="text-muted-foreground mb-6">
                Votre demande a bien été envoyée. Nous reviendrons vers vous par email dès que possible.
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => { setSent(false); setForm({ firstName: '', lastName: '', email: '', profile: '', subject: '', message: '', company: '' }); }} variant="secondary" data-testid="contact-new-message-btn">
                  Nouveau message
                </Button>
                <Link to="/">
                  <Button variant="outline" data-testid="contact-home-btn">Accueil</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ContactHeader />
      <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12" data-testid="contact-page">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">

          {/* Left column: hero + reassurance + help */}
          <div className="lg:col-span-2 space-y-8">
            <div className="space-y-3">
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight" data-testid="contact-title">
                Une question sur QCMAntitriche ? Parlons-en.
              </h1>
              <p className="text-muted-foreground leading-relaxed">
                Demande d'information, question avant inscription, besoin d'aide, retour d'expérience ou partenariat : envoyez votre message via ce formulaire.
              </p>
              <p className="text-sm text-muted-foreground/80">
                Chaque demande est lue avec attention. Nous répondons par email.
              </p>
            </div>

            {/* Micro reassurance */}
            <div className="space-y-2.5" data-testid="contact-reassurance">
              {REASSURANCE.map((item) => (
                <div key={item} className="flex items-center gap-2.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>

            {/* Help box */}
            <Card className="bg-muted/30 border-border" data-testid="contact-help-box">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <HelpCircle className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-sm">Vous pouvez nous écrire pour :</span>
                </div>
                <ul className="space-y-1.5">
                  {HELP_ITEMS.map((item) => (
                    <li key={item} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary mt-1.5 w-1 h-1 rounded-full bg-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Right column: form */}
          <div className="lg:col-span-3">
            <Card className="bg-card border-border" data-testid="contact-form-card">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-bold">Envoyer un message</h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  {/* Honeypot - hidden */}
                  <div className="absolute opacity-0 -z-10" aria-hidden="true" tabIndex={-1}>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={(e) => set('company', e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">Prénom *</Label>
                      <Input
                        id="firstName"
                        data-testid="contact-firstName"
                        value={form.firstName}
                        onChange={(e) => set('firstName', e.target.value)}
                        placeholder="Votre prénom"
                        className={errors.firstName ? 'border-destructive' : ''}
                      />
                      {errors.firstName && <p className="text-xs text-destructive mt-1" role="alert">{errors.firstName}</p>}
                    </div>
                    <div>
                      <Label htmlFor="lastName">Nom</Label>
                      <Input
                        id="lastName"
                        data-testid="contact-lastName"
                        value={form.lastName}
                        onChange={(e) => set('lastName', e.target.value)}
                        placeholder="Votre nom"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      data-testid="contact-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => set('email', e.target.value)}
                      placeholder="vous@exemple.com"
                      className={errors.email ? 'border-destructive' : ''}
                    />
                    {errors.email && <p className="text-xs text-destructive mt-1" role="alert">{errors.email}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Profil</Label>
                      <Select value={form.profile} onValueChange={(v) => set('profile', v)}>
                        <SelectTrigger data-testid="contact-profile">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          {PROFILE_OPTIONS.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Sujet *</Label>
                      <Select value={form.subject} onValueChange={(v) => set('subject', v)}>
                        <SelectTrigger data-testid="contact-subject" className={errors.subject ? 'border-destructive' : ''}>
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          {SUBJECT_OPTIONS.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.subject && <p className="text-xs text-destructive mt-1" role="alert">{errors.subject}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message"
                      value={form.message}
                      onChange={(e) => set('message', e.target.value)}
                      placeholder="Décrivez votre demande"
                      className={`min-h-[140px] ${errors.message ? 'border-destructive' : ''}`}
                      maxLength={5000}
                    />
                    <div className="flex justify-between mt-1">
                      {errors.message ? (
                        <p className="text-xs text-destructive" role="alert">{errors.message}</p>
                      ) : <span />}
                      <span className="text-xs text-muted-foreground">{form.message.length}/5000</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={sending}
                    className="w-full bg-primary text-primary-foreground hover:bg-blue-600 h-11"
                  >
                    {sending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Envoyer ma demande</>
                    )}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center" data-testid="contact-privacy">
                    <Shield className="w-3 h-3 inline mr-1" />
                    En envoyant ce formulaire, vous acceptez que vos données soient utilisées uniquement pour traiter votre demande. Aucun usage commercial abusif.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

function ContactHeader() {
  return (
    <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-sm border-b border-border">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 h-12">
        <Link to="/" className="flex items-center gap-1 font-black text-base tracking-tight" data-testid="contact-logo">
          <span className="text-primary">QCM</span><span>AntiTriche</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-sm text-muted-foreground" data-testid="contact-nav-home">
              Accueil
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button variant="outline" size="sm" className="text-sm" data-testid="contact-nav-app">
              Mon espace
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}
